import { createSelector, createSlice } from "@reduxjs/toolkit";

import {
    createField,
    getAgeLevels,
    getBracketTypes,
    getDesignations,
    getFields,
    getNotice,
    getPhotos,
    getPositions,
    getSeasons,
    getSettings,
    getSites,
    getUmpires,
    getUserLevels,
    updateField,
    updateNotice,
} from "./globalActions";

import { authActions } from "../auth/authSlice";

import { RootState } from "../../store";
import {
    IAgeLevel,
    IDesignation,
    IField,
    INotice,
    IPhoto,
    IPlayoffBracket,
    IPosition,
    ISeason,
    ISite,
    ISiteSetting,
    IUser,
    IUserLevel,
} from "../../types/api";
import { IStateDefaults } from "../../types/shared";

export interface GlobalState extends IStateDefaults {
    ageLevels: IAgeLevel[];
    umpires: Array<{ id: IUser["id"]; name: string }>;
    settings: ISiteSetting[];
    fields: IField[];
    seasons: ISeason[];
    positions: IPosition[];
    notice?: INotice;
    photos: IPhoto[];
    designations: IDesignation[];
    bracketTypes: IPlayoffBracket[];
    sites: ISite[];
    userLevels: IUserLevel[];
}

const initialState: GlobalState = {
    loading: false,
    success: false,
    ageLevels: [],
    umpires: [],
    settings: [],
    fields: [],
    seasons: [],
    photos: [],
    designations: [],
    bracketTypes: [],
    positions: [],
    notice: undefined,
    sites: [],
    userLevels: [],
};

const globalSlice = createSlice({
    name: "global",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.umpires = [];
            })
            .addCase(getAgeLevels.fulfilled, (state, { payload }) => {
                state.ageLevels = payload.ageLevels;
            })
            .addCase(getUmpires.fulfilled, (state, { payload }) => {
                state.umpires = payload;
            })
            .addCase(getSettings.fulfilled, (state, { payload }) => {
                state.settings = payload;
            })
            .addCase(getFields.fulfilled, (state, { payload }) => {
                state.fields = payload;
            })
            .addCase(createField.pending, state => {
                state.loading = true;
            })
            .addCase(createField.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.fields = payload;
            })
            .addCase(createField.rejected, state => {
                state.loading = false;
            })
            .addCase(updateField.pending, state => {
                state.loading = true;
            })
            .addCase(updateField.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.fields = payload;
            })
            .addCase(updateField.rejected, state => {
                state.loading = false;
            })
            .addCase(getSeasons.fulfilled, (state, { payload }) => {
                state.seasons = payload;
            })
            .addCase(getNotice.fulfilled, (state, { payload }) => {
                state.notice = payload ? payload : undefined;
            })
            .addCase(updateNotice.pending, state => {
                state.loading = true;
            })
            .addCase(updateNotice.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.notice = payload ? payload : undefined;
            })
            .addCase(updateNotice.rejected, state => {
                state.loading = false;
            })
            .addCase(getPhotos.fulfilled, (state, { payload }) => {
                state.photos = payload;
            })
            .addCase(getDesignations.fulfilled, (state, { payload }) => {
                state.designations = payload;
            })
            .addCase(getBracketTypes.fulfilled, (state, { payload }) => {
                state.bracketTypes = payload;
            })
            .addCase(getPositions.fulfilled, (state, { payload }) => {
                state.positions = payload;
            })
            .addCase(getSites.fulfilled, (state, { payload }) => {
                state.sites = payload;
            })
            .addCase(getUserLevels.fulfilled, (state, { payload }) => {
                state.userLevels = payload;
            });
    },
});

export const globalActions = {
    ...globalSlice.actions,
    getAgeLevels: getAgeLevels,
    getUmpires: getUmpires,
    getSettings: getSettings,
    getFields: getFields,
    createField: createField,
    updateField: updateField,
    getSeasons: getSeasons,
    getNotice: getNotice,
    updateNotice: updateNotice,
    getPhotos: getPhotos,
    getDesignations: getDesignations,
    getBracketTypes: getBracketTypes,
    getPositions: getPositions,
    getSites: getSites,
    getUserLevels: getUserLevels,
};

export default globalSlice.reducer;

export const selectAgeLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.ageLevels,
);

export const selectSetting = createSelector(
    [
        (state: RootState) => state.global.settings,
        (state: RootState, settingName: string) => settingName,
    ],
    (settings, settingName) =>
        settings.find(setting => setting.settingName === settingName),
);

export const selectAllSettings = createSelector(
    [(state: RootState) => state.global],
    global => global.settings,
);

export const selectField = createSelector(
    [
        (state: RootState) => state.global.fields,
        (state: RootState, fieldId: number) => fieldId,
    ],
    (fields, fieldId) => fields.find(f => f.id === fieldId),
);

export const selectFields = createSelector(
    [(state: RootState) => state.global],
    global => [...global.fields].sort((a, b) => a.name.localeCompare(b.name)),
);

export const selectUmpires = createSelector(
    [(state: RootState) => state.global],
    global => global.umpires,
);

export const selectSeasons = createSelector(
    [(state: RootState) => state.global],
    global => global.seasons,
);

export const selectNotice = createSelector(
    [(state: RootState) => state.global],
    global => global.notice,
);

export const selectDesignations = createSelector(
    [(state: RootState) => state.global],
    global => global.designations,
);

export const selectBracketTypes = createSelector(
    [(state: RootState) => state.global],
    global => global.bracketTypes,
);

export const selectPositions = createSelector(
    [(state: RootState) => state.global],
    global => global.positions,
);

export const selectSite = createSelector(
    [
        (state: RootState) => state.global.sites,
        (state: RootState, siteId: number) => siteId,
    ],
    (sites, siteId) => sites.find(site => site.id === siteId),
);

export const selectAllSites = createSelector(
    [(state: RootState) => state.global],
    global => global.sites,
);

export const selectAllUserLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.userLevels,
);
